import React, {useEffect, useMemo, useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';
import createContentApi from './contentApi';
import {contentConfig} from './contentConfig';
import './contentEditor.css';

export default function ContentListManager({
    kind = 'page',
    editorPath,
    classicUrl,
    storefrontUrl,
    apiClient,
    onNavigate,
}) {
    const auth = useAuth();
    const api = useMemo(
        () => apiClient || createContentApi({fetch: auth.fetch, apiUrl: auth.API_URL}),
        [apiClient, auth.API_URL, auth.fetch],
    );
    const config = contentConfig(kind);
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState({page: 1, total: 0, hasNext: false});
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [title, setTitle] = useState('');
    const [error, setError] = useState('');
    const navigate = onNavigate || (nextUrl => window.location.assign(nextUrl));

    const load = async page => {
        setLoading(true);
        setError('');
        try {
            const result = await api.list(kind, page);
            setItems(result.data);
            setMeta(result.meta);
        } catch (nextError) {
            setError(nextError.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(1); }, [api, kind]);

    const create = async event => {
        event.preventDefault();
        setCreating(true);
        setError('');
        try {
            const result = await api.create(kind, {title: title.trim()});
            navigate(`${editorPath}/${result.data.resource.uuid}?uiVersion=2`);
        } catch (nextError) {
            setError(nextError.message);
            setCreating(false);
        }
    };

    return <main className="content-list-manager">
        <header className="content-editor-topbar">
            <div className="content-editor-heading">
                <span>Seller content</span>
                <strong>{kind === 'blog' ? 'Blog Posts' : 'Custom Pages'}</strong>
            </div>
            <a className="button secondary" href={classicUrl}>Use classic list</a>
        </header>
        {error && <p className="flash error" role="alert">{error}</p>}
        <section className="content-editor-panel" aria-labelledby="create-content-title">
            <h2 id="create-content-title">Create {config.label.toLowerCase()}</h2>
            <form className="content-create-form group" onSubmit={create}>
                <label htmlFor="new-content-title">Title</label>
                <input type="text" id="new-content-title" value={title} minLength="4" required
                    onChange={event => setTitle(event.target.value)} />
                <button type="submit" className="button"
                    disabled={creating || title.trim().length < 4}>
                    {creating ? 'Creating…' : 'Create and edit'}
                </button>
            </form>
        </section>
        <section className="content-editor-panel" aria-labelledby="content-list-title">
            <header className="content-editor-panel-header">
                <div>
                    <h2 id="content-list-title">{kind === 'blog' ? 'Blog Posts' : 'Pages'}</h2>
                    <p>{meta.total} total</p>
                </div>
            </header>
            {loading && <p role="status">Loading {config.collectionLabel.toLowerCase()}…</p>}
            {!loading && items.length === 0 && <p className="help">
                No {config.collectionLabel.toLowerCase()} yet.
            </p>}
            {!loading && items.length > 0 && <div className="content-list-table-wrap">
                <table>
                    <thead><tr>
                        <th>Title</th><th>Status</th><th>Last updated</th><th>Actions</th>
                    </tr></thead>
                    <tbody>{items.map(item => <tr key={item.uuid}>
                        <td data-label="Title">
                            <strong>{item.title}</strong><small>/{item.slug}</small>
                        </td>
                        <td data-label="Status">
                            {item.isPublished ? 'Published' : 'Draft'}
                            {kind === 'blog' && item.redContentReview?.status
                                ? ` · RED ${item.redContentReview.status}` : ''}
                        </td>
                        <td data-label="Last updated">
                            {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '—'}
                        </td>
                        <td data-label="Actions" className="content-list-actions">
                            <a className="button small"
                                href={`${editorPath}/${item.uuid}?uiVersion=2`}>Edit</a>
                            {item.isPublished && <a className="button small secondary" href={
                                `${storefrontUrl}/${kind === 'blog' ? 'blog' : 'page'}/${item.slug}`
                            }
                                target="_blank" rel="noreferrer">View</a>}
                            {kind === 'page' && item.isPublished && <form method="post"
                                action="/account/landingpage/link/add"
                                encType="multipart/form-data">
                                <input type="hidden" name="title" value={item.title} />
                                <input type="hidden" name="url"
                                    value={`${storefrontUrl}/page/${item.slug}`} />
                                <input type="hidden" name="after"
                                    value={`${editorPath}?uiVersion=2`} />
                                <button type="submit" className="button small secondary">
                                    Add to Link Tree
                                </button>
                            </form>}
                        </td>
                    </tr>)}</tbody>
                </table>
            </div>}
            <div className="content-list-pagination">
                <button type="button" className="button secondary"
                    disabled={loading || meta.page <= 1} onClick={() => load(meta.page - 1)}>
                    Previous
                </button>
                <span>Page {meta.page || 1}</span>
                <button type="button" className="button secondary"
                    disabled={loading || !meta.hasNext} onClick={() => load(meta.page + 1)}>
                    Next
                </button>
            </div>
        </section>
        {kind === 'blog' && <section className="content-editor-panel"
            aria-labelledby="blog-link-tree-title">
            <h2 id="blog-link-tree-title">Add the blog to Link Tree</h2>
            <form method="post" action="/account/landingpage/link/add"
                encType="multipart/form-data" className="content-link-tree-form">
                <input type="hidden" name="title" value="Blog" />
                <input type="hidden" name="icon" value="fa-solid fa-blog" />
                <input type="hidden" name="url" value={`${storefrontUrl}/blog`} />
                <input type="hidden" name="after" value={`${editorPath}?uiVersion=2`} />
                <button type="submit" className="button secondary">Add to Link Tree</button>
            </form>
        </section>}
    </main>;
}
